import { mapGetters, mapActions } from 'vuex'

import { metaInfoBuilder, kebabToSnake, jsonLdBuilder } from '../utilities'
import { PAGES, BLOCKS } from '../content-types'

const type = PAGES.PRODUCT

export const ProductDetailsComponent = {
  name: 'PageComponent',
  data() {
    return {
      page: null,
      centralizedBlocks: []
    }
  },
  computed: {
    ...mapGetters('prismic', ['getPage', 'getBlock', 'getAlternateLanguages']),
    ...mapGetters('konami', ['code', 'overlay']),
    components() {
      const content = this.page ? this.page.content : []
      return content
        .map(component => {
          const block = this.centralizedBlocks.find(b => component.component === kebabToSnake(b))
          return this.getBlock(block) || component
        })
    },
    isBlocksLoaded(): boolean {
      return this.centralizedBlocks.every(block => !!this.getBlock(block))
    },
    modifiedComponents() {
      if (this.components.length) {
        let header = this.components.find((c) => c.component === 'product_header')
        header = {
          ...header,
          item:
            header && header.item
              ? {
                ...header.item,
                iconCtaUrl: this.page && this.page.main ? this.page.main.icons_store_url : '',
                iconCtaLabel: this.page && this.page.main ? this.page.main.icons_store_label : ''
              }
              : {}
        }
        const index = this.components.findIndex((c) => c.component === 'product_header')
        const components = [...this.components]
        components[index] = header
        return components
      } else {
        return []
      }
    },
    productJsonLd() {
      return this.getJsonLd()
    },
    konami() {
      return this.page ? this.page.konami : null
    }
  },
  methods: {
    ...mapActions('prismic', ['setPage', 'setBlock']),
    ...mapActions('konami', ['setCode', 'setOverlay']),
    getJsonLd() {
      const page = this.page || this.getPage({ type, uid: this.$route.params.uid })
      if (page) {
        return jsonLdBuilder(page, this.$route)
      } else {
        return this.setPage({ type, uid: this.$route.params.uid })
          .then(() => {
            const refetchedPage = this.getPage({ uid: this.$route.params.uid, type })
            return refetchedPage ? jsonLdBuilder(refetchedPage, this.$route) : {}
          })
      }
    },
    async setContent(): Promise<void> {
      const prefetchedPage = this.getPage({ uid: this.$route.params.uid, type })
      let page

      if (prefetchedPage && this.isBlocksLoaded) {
        page = prefetchedPage
      } else {
        const blockRequests = this.centralizedBlocks.map(uid => this.setBlock(uid))
        const pageRequest = this.setPage({ type, uid: this.$route.params.uid })

        await Promise.all([pageRequest, ...blockRequests])
        page = this.getPage({ uid: this.$route.params.uid, type })
      }

      this.setCode({ code: page && page.konami ? page.konami.code : null })
      this.setOverlay({ overlay: !!(page && page.konami && page.konami.code) })
      this.page = page
    }
  },
  async serverPrefetch(): Promise<void> {
    await this.setContent()
    
    const isProductUnavailable = this.components.some(
      ({ component }) => component === 'product_header_error'
    )
    if (isProductUnavailable || !this.page) {
      const error = new Error('Product not found')
      ;(error as any).code = 404
      throw error
    }
  },
  async created(): Promise<void> {
    await this.setContent()
  },
  metaInfo() {
    const page = this.page

    if (!page) return {}

    if (!page.main || !page.main.product || !page.main.product.product) {
      return metaInfoBuilder(page, this.$route, this.getAlternateLanguages({type, uid: this.$route.params.uid}))
    }

    const product = Object.values<any>(page.main.product.product)[0]
    const isWatchesCategory = product ? product.attribute_set_name === 'Watches' : false
    const details = isWatchesCategory ? this.$t('PDP seo details', {diameter: product.diameter, material: product.material}) : ''
    const itemCategory = isWatchesCategory ? this.$t('Watch') : this.$t('Strap')

    const builderData = product ? {
      title: {
        name: product.name,
        details
      },
      description: {
        name: product.name,
        details,
        itemCategory
      }
    } : null
    return metaInfoBuilder(page, this.$route, this.getAlternateLanguages({type, uid: this.$route.params.uid}), type, builderData, this)
  }
}
